import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  RefreshCw,
  Search,
  Download,
  Eye,
  ChevronUp,
  ChevronDown,
  HelpCircle,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { usersApi, eventsApi, adminApi } from '../../services/api';
import {
  IEvent,
  AppUser,
  UserStats,
  UserStatsUserRow,
  UserStatsUsersFilterOptions,
  UserStatsUsersCounts,
} from '../../types';
import { useAuth } from '../../contexts/AuthContext';

type TabKey = 'funnel' | 'patterns' | 'users';
type SortDir = 'asc' | 'desc';
type FilterKey = 'all' | 'active' | 'inactive' | 'never_chatted' | 'unverified';
type UserSortCol = 'name' | 'email' | 'status' | 'verified' | 'occupation' | 'businessNature';
type VerifiedStatus = 'verified' | 'pending' | 'unverified';
type UserStatus = 'active' | 'inactive' | 'never_chatted';

const EMPTY_FUNNEL = {
  registered: 0,
  registeredDelta: 0,
  verified: 0,
  verifiedRate: 0,
  active: 0,
  activeRate: 0,
  returning: 0,
  returningRate: 0,
  neverChatted: 0,
  neverChattedRate: 0,
};

const EMPTY_COUNTS: UserStatsUsersCounts = {
  all: 0,
  active: 0,
  inactive: 0,
  neverChatted: 0,
  unverified: 0,
};

const EMPTY_FILTER_OPTIONS: UserStatsUsersFilterOptions = {
  languages: [],
  occupations: [],
  businessNatures: [],
};

function InfoTooltip({ text, width = 240 }: { text: string; width?: number }) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!show) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setShow(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  return (
    <span ref={ref} className="relative inline-flex items-center ml-1.5 cursor-pointer">
      <HelpCircle
        className="w-3.5 h-3.5 text-gray-300 hover:text-gray-400 transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(s => !s)}
      />
      {show && (
        <div
          className="absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 z-50 rounded-xl bg-gray-900 text-gray-100 text-xs leading-relaxed shadow-xl p-3"
          style={{ width }}
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </span>
  );
}

interface FunnelCardProps {
  step: string;
  label: string;
  value: number | string;
  delta?: number;
  subtext: string;
  subtextColor?: string;
  bgClass: string;
  textClass: string;
  tooltip: string;
}

function FunnelCard({
  step,
  label,
  value,
  delta,
  subtext,
  subtextColor,
  bgClass,
  textClass,
  tooltip,
}: FunnelCardProps) {
  return (
    <div className={`flex-1 min-w-0 rounded-xl p-4 ${bgClass} relative`}>
      <div className={`absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none ${textClass}`}>{step}</div>
      <div className={`text-xs font-medium uppercase tracking-wide mb-1.5 flex items-center relative z-10 ${textClass} opacity-80`}>
        {label}
        <InfoTooltip text={tooltip} />
      </div>
      <div className="flex items-baseline gap-2 relative z-10">
        <span className={`text-3xl font-semibold leading-none ${textClass}`}>{value}</span>
        {delta !== undefined && delta !== 0 && (
          <span className={`text-xs font-medium ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}
          </span>
        )}
      </div>
      <div className="text-xs mt-1 relative z-10" style={{ color: subtextColor || undefined }}>
        <span className={!subtextColor ? `${textClass} opacity-60` : ''}>{subtext}</span>
      </div>
    </div>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data.length) return null;

  const w = 120;
  const h = 32;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => `${(i / Math.max(data.length - 1, 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`)
    .join(' ');
  const areaPoints = `0,${h} ${points} ${w},${h}`;
  const lastY = h - ((data[data.length - 1] - min) / range) * (h - 4) - 2;

  return (
    <svg width={w} height={h} className="block flex-shrink-0">
      <polyline points={areaPoints} fill={color} opacity="0.1" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}

function EngagementRow({
  label,
  value,
  unit,
  trend,
  color,
  tooltip,
}: {
  label: string;
  value: number;
  unit: string;
  trend: number[];
  color: string;
  tooltip: string;
}) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-0">
      <div>
        <div className="text-xs text-gray-500 mb-0.5 flex items-center">
          {label}
          <InfoTooltip text={tooltip} width={260} />
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold text-gray-900">{value}</span>
          <span className="text-xs text-gray-400">{unit}</span>
        </div>
      </div>
      <Sparkline data={trend} color={color} />
    </div>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  const map: Record<UserStatus, { label: string; classes: string }> = {
    active: { label: 'Active', classes: 'bg-emerald-50 text-emerald-700' },
    inactive: { label: 'Inactive', classes: 'bg-gray-100 text-gray-500' },
    never_chatted: { label: 'Never chatted', classes: 'bg-rose-50 text-rose-700' },
  };
  const s = map[status] || map.inactive;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${s.classes}`}>{s.label}</span>;
}

function VerifiedBadge({ status }: { status: VerifiedStatus }) {
  const map: Record<VerifiedStatus, { icon: React.ReactNode; classes: string }> = {
    verified: { icon: <CheckCircle className="w-3.5 h-3.5" />, classes: 'text-emerald-600 bg-emerald-50' },
    pending: { icon: <Clock className="w-3.5 h-3.5" />, classes: 'text-amber-600 bg-amber-50' },
    unverified: { icon: <AlertCircle className="w-3.5 h-3.5" />, classes: 'text-gray-400 bg-gray-100' },
  };
  const s = map[status] || map.unverified;
  return <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${s.classes}`}>{s.icon}</span>;
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-sm inline-block" style={{ background: p.fill ?? p.color }} />
          <span className="text-gray-500">{p.name}:</span>
          <span className="font-medium text-gray-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function toRelativeTime(dateString: string | null): string {
  if (!dateString) return '—';
  const now = Date.now();
  const ts = new Date(dateString).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffMs = now - ts;
  const diffMin = Math.floor(diffMs / (1000 * 60));
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hour${diffH > 1 ? 's' : ''} ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD} day${diffD > 1 ? 's' : ''} ago`;
}

function avatarColor(name: string) {
  const colors = [
    'bg-indigo-100 text-indigo-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-violet-100 text-violet-700',
    'bg-sky-100 text-sky-700',
    'bg-rose-100 text-rose-700',
  ];
  return colors[(name?.charCodeAt(0) ?? 0) % colors.length];
}

function getInitials(name: string) {
  return (name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function getLanguageBadgeClasses(languageCode?: string | null) {
  const map: Record<string, string> = {
    en: 'bg-sky-50 text-sky-700 border-sky-200',
    hi: 'bg-amber-50 text-amber-700 border-amber-200',
    kn: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bn: 'bg-rose-50 text-rose-700 border-rose-200',
    ta: 'bg-violet-50 text-violet-700 border-violet-200',
    te: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  };
  return map[(languageCode || '').toLowerCase()] || 'bg-indigo-50 text-indigo-700 border-indigo-200';
}

function LanguageBadge({
  languageCode,
  languageName,
}: {
  languageCode?: string | null;
  languageName?: string | null;
}) {
  const label = languageName || languageCode;
  if (!label) return null;

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${getLanguageBadgeClasses(languageCode)}`}
    >
      {label}
    </span>
  );
}

const UserStatistics: React.FC = () => {
  const { isAdmin, user } = useAuth();

  const [stats, setStats] = useState<UserStats | null>(null);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('funnel');

  const [rows, setRows] = useState<UserStatsUserRow[]>([]);
  const [counts, setCounts] = useState<UserStatsUsersCounts>(EMPTY_COUNTS);
  const [filterOptions, setFilterOptions] = useState<UserStatsUsersFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [rowLoading, setRowLoading] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tablePages, setTablePages] = useState(0);
  const [tableTotal, setTableTotal] = useState(0);
  const tableLimit = 20;

  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState<FilterKey>('all');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedOccupation, setSelectedOccupation] = useState('');
  const [selectedBusinessNature, setSelectedBusinessNature] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<UserSortCol>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    const timer = setTimeout(() => setUserSearch(userSearchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [userSearchInput]);

  useEffect(() => {
    if (isAdmin) fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedClientId, user?._id]);

  useEffect(() => {
    if (isAdmin && selectedEventId) setSelectedEventId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setClients(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const response = await eventsApi.getAll(params);
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const buildScopeParams = useCallback(() => {
    const params: any = {};
    if (isAdmin && selectedClientId) params.clientId = selectedClientId;
    else if (!isAdmin && user?._id) params.clientId = user._id;
    if (selectedEventId) params.eventId = selectedEventId;
    return params;
  }, [isAdmin, selectedClientId, selectedEventId, user?._id]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setStats(null);
    try {
      const response = await usersApi.getGeneralStatistics(buildScopeParams());
      if (response.data.success && response.data.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [buildScopeParams]);

  const fetchUsersTable = useCallback(async (page = 1) => {
    setRowLoading(true);
    setExpandedUserId(null);
    try {
      const params: any = {
        ...buildScopeParams(),
        page,
        limit: tableLimit,
      };

      if (userSearch) params.search = userSearch;
      if (userFilter === 'active') params.status = 'active';
      if (userFilter === 'inactive') params.status = 'inactive';
      if (userFilter === 'never_chatted') params.status = 'never_chatted';
      if (userFilter === 'unverified') params.verified = 'unverified';
      if (selectedLanguage) params.language = selectedLanguage;
      if (selectedOccupation) params.occupation = selectedOccupation;
      if (selectedBusinessNature) params.businessNature = selectedBusinessNature;

      const response = await usersApi.getStatisticsUsers(params);
      if (response.data.success && response.data.data) {
        const payload = response.data.data;
        setRows(payload.rows || []);
        setCounts(payload.counts || EMPTY_COUNTS);

        const p = payload.pagination || response.data.pagination;
        setTablePage(p?.page ?? page);
        setTablePages(p?.pages ?? 0);
        setTableTotal(p?.total ?? 0);
      }
    } catch (error) {
      console.error('Error fetching user statistics rows:', error);
      toast.error('Failed to load users');
    } finally {
      setRowLoading(false);
    }
  }, [
    buildScopeParams,
    userSearch,
    userFilter,
    selectedLanguage,
    selectedOccupation,
    selectedBusinessNature,
  ]);

  const fetchUsersCounts = useCallback(async () => {
    try {
      const response = await usersApi.getStatisticsUsers({
        ...buildScopeParams(),
        page: 1,
        limit: 1,
      });

      if (response.data.success && response.data.data) {
        const payload = response.data.data;
        setCounts(payload.counts || EMPTY_COUNTS);
        setFilterOptions(payload.filterOptions || EMPTY_FILTER_OPTIONS);

        const p = payload.pagination || response.data.pagination;
        if (p) {
          setTableTotal(p.total ?? 0);
          setTablePages(p.pages ?? 0);
        }
      }
    } catch (error) {
      console.error('Error prefetching user counts:', error);
    }
  }, [buildScopeParams]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUsersCounts();
  }, [fetchUsersCounts]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    fetchUsersTable(1);
  }, [
    userSearch,
    userFilter,
    selectedLanguage,
    selectedOccupation,
    selectedBusinessNature,
    selectedClientId,
    selectedEventId,
    activeTab,
    fetchUsersTable,
  ]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av: any = a[sortCol];
      const bv: any = b[sortCol];

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      return sortDir === 'desc'
        ? String(bv || '').localeCompare(String(av || ''), undefined, { sensitivity: 'base' })
        : String(av || '').localeCompare(String(bv || ''), undefined, { sensitivity: 'base' });
    });
  }, [rows, sortCol, sortDir]);

  const handleSort = (col: UserSortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'funnel', label: 'Registration funnel' },
    { key: 'patterns', label: 'User patterns' },
    { key: 'users', label: 'Users', count: counts.all },
  ];

  const selectedEvent = events.find((e) => (e._id || e.eventId) === selectedEventId);
  const showVerifiedCard = selectedEventId ? !!selectedEvent?.requiresEmailVerification : true;

  const funnel = stats?.funnel || EMPTY_FUNNEL;
  const dailyActivity = stats?.dailyActivity || stats?.signupSigninData?.slice(-14) || [];
  const engagementDepth = stats?.engagementDepth || {
    avgSessionsPerUser: Number(stats?.avgSessionsPerUser || 0),
    avgMessagesPerUser: Number(stats?.avgMessagesPerUser || 0),
    avgSessionDurationMinutes: 0,
    callbackRequestRate: 0,
    callbackRequestCount: 0,
    avgSessionsTrend: [],
    avgMessagesTrend: [],
    avgSessionDurationTrend: [],
  };

  const peakHours = stats?.patterns?.peakHours || [];
  const languages = stats?.usersByLanguage || [];
  const audienceBreakdown = stats?.patterns?.audienceBreakdown || [];

  const peakMax = Math.max(...peakHours.map((h) => h.sessions || 0), 1);
  let peakUsageHourLabel = '--';
  if (peakHours.length > 0) {
    let topHour = peakHours[0];
    for (const hourBucket of peakHours) {
      if ((hourBucket.sessions || 0) > (topHour.sessions || 0)) {
        topHour = hourBucket;
      }
    }
    peakUsageHourLabel = (topHour.sessions || 0) > 0 ? topHour.label : '--';
  }

  const registeredStep = '①';
  const verifiedStep = '②';
  const activeStep = showVerifiedCard ? '③' : '②';
  const returningStep = showVerifiedCard ? '④' : '③';
  const neverChattedStep = showVerifiedCard ? '⑤' : '④';
  const avgSessionsStep = showVerifiedCard ? '⑥' : '⑤';

  const userCols: { key: UserSortCol; label: string; width: string }[] = [
    { key: 'name', label: 'Name', width: '20%' },
    { key: 'email', label: 'Email', width: '18%' },
    { key: 'status', label: 'Status', width: '11%' },
    { key: 'verified', label: 'Verified', width: '9%' },
    { key: 'occupation', label: 'Occupation', width: '14%' },
    { key: 'businessNature', label: 'Business Nature', width: '16%' },
  ];

  const filterChips: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All users', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
    { key: 'never_chatted', label: 'Never chatted', count: counts.neverChatted },
    { key: 'unverified', label: 'Unverified', count: counts.unverified },
  ];

  const userTableColSpan = userCols.length + 1;
  const filterSelectClass = (isActive: boolean) =>
    `h-8 rounded-lg border px-2.5 text-xs font-medium bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
      isActive
        ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
        : 'border-gray-200 text-gray-500'
    }`;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">User Statistics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {selectedEventId
              ? selectedEvent?.name ?? 'Selected Knowledge Assistant'
              : selectedClientId
              ? `All Knowledge Assistants for ${clients.find((c) => c._id === selectedClientId)?.name ?? 'selected client'}`
              : 'Global statistics across all Knowledge Assistants'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <select
              value={selectedClientId}
              onChange={(e) => {
                setSelectedClientId(e.target.value);
                setSelectedEventId('');
              }}
              className="h-9 px-3 border border-border rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          )}

          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="h-9 px-3 border border-border rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Knowledge Assistants</option>
            {events.map((e) => (
              <option key={e._id || e.eventId} value={e._id || e.eventId}>
                {e.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              fetchStats();
              if (activeTab === 'users') fetchUsersTable(tablePage);
            }}
            disabled={loading}
            className="h-9 px-3 flex items-center gap-1.5 border border-border rounded-lg text-sm bg-white text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-200">
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 text-sm transition-all border-b-2 -mb-px ${
                isActive
                  ? 'border-indigo-500 text-indigo-700 font-semibold'
                  : 'border-transparent text-gray-500 font-normal hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          Loading statistics…
        </div>
      )}

      {/* Empty */}
      {!loading && !stats && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <Users className="w-10 h-10 text-gray-200 mb-3" />
          <p className="text-sm">No data available</p>
        </div>
      )}

      {/* Funnel Tab */}
      {!loading && stats && activeTab === 'funnel' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Registration funnel</h2>
            <p className="text-xs text-gray-500 mt-0.5">User lifecycle from signup to engagement</p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <FunnelCard
              step={registeredStep}
              label="Registered"
              value={funnel.registered}
              delta={funnel.registeredDelta}
              subtext={`+${funnel.registeredDelta} this period`}
              bgClass="bg-indigo-50"
              textClass="text-indigo-700"
              tooltip="Total user accounts created for this Knowledge Assistant, till date."
            />
            {showVerifiedCard && (
              <FunnelCard
                step={verifiedStep}
                label="Verified"
                value={funnel.verified}
                subtext={`${funnel.verifiedRate}% of registered`}
                subtextColor={funnel.verifiedRate >= 80 ? '#047857' : funnel.verifiedRate >= 50 ? '#B45309' : '#BE123C'}
                bgClass="bg-sky-50"
                textClass="text-sky-700"
                tooltip="Users who completed email verification."
              />
            )}
            <FunnelCard
              step={activeStep}
              label="Active"
              value={funnel.active}
              subtext={`${funnel.activeRate}% adoption rate`}
              subtextColor={funnel.activeRate >= 60 ? '#047857' : funnel.activeRate >= 30 ? '#B45309' : '#BE123C'}
              bgClass="bg-emerald-50"
              textClass="text-emerald-700"
              tooltip="Users who started at least one chat session."
            />
            <FunnelCard
              step={returningStep}
              label="Returning"
              value={funnel.returning}
              subtext={`${funnel.returningRate}% of active`}
              subtextColor={funnel.returningRate >= 40 ? '#047857' : funnel.returningRate >= 20 ? '#B45309' : '#BE123C'}
              bgClass="bg-violet-50"
              textClass="text-violet-700"
              tooltip="Users who signed in on at least 2 distinct days."
            />
            <FunnelCard
              step={neverChattedStep}
              label="Never chatted"
              value={funnel.neverChatted}
              subtext={`${funnel.neverChattedRate}% drop-off`}
              subtextColor={funnel.neverChattedRate > 30 ? '#BE123C' : funnel.neverChattedRate > 15 ? '#B45309' : '#047857'}
              bgClass="bg-rose-50"
              textClass="text-rose-700"
              tooltip="Registered users who never started a chat session."
            />
            <FunnelCard
              step={avgSessionsStep}
              label="Avg Sessions / User"
              value={Number(engagementDepth.avgSessionsPerUser || 0).toFixed(1)}
              subtext={`${Number(engagementDepth.avgMessagesPerUser || 0).toFixed(1)} avg messages / user`}
              bgClass="bg-amber-50"
              textClass="text-amber-700"
              tooltip="Average sessions per registered user in current scope."
            />
          </div>

          <div className="grid grid-cols-[1.6fr_1fr] gap-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Daily activity</h3>
              <p className="text-xs text-gray-500 mb-4">New registrations and sign-in events — last 14 days</p>
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-indigo-500 inline-block" />Signups
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-emerald-400 inline-block" />Sign-ins
                </span>
              </div>
              {dailyActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dailyActivity.map((d) => ({ ...d, date: dayjs(d.date).format('MMM D') }))} barGap={4} barCategoryGap="28%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={(props: any) => <ChartTooltip {...props} />} />
                    <Bar dataKey="signups" name="Signups" fill="#6366F1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="signins" name="Sign-ins" fill="#34D399" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[220px] text-sm text-gray-400">No activity data</div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Engagement depth</h3>
              <p className="text-xs text-gray-500 mb-3">Per-user averages since Knowledge Assistant creation</p>

              <EngagementRow
                label="Avg sessions / user"
                value={Number(engagementDepth.avgSessionsPerUser || 0)}
                unit="sessions"
                trend={engagementDepth.avgSessionsTrend || []}
                color="#6366F1"
                tooltip="Average number of chat sessions per active user in the selected period."
              />
              <EngagementRow
                label="Avg messages / user"
                value={Number(engagementDepth.avgMessagesPerUser || 0)}
                unit="messages"
                trend={engagementDepth.avgMessagesTrend || []}
                color="#10B981"
                tooltip="Average number of messages exchanged per active user across sessions."
              />
              {/* <EngagementRow
                label="Avg session duration"
                value={Number(engagementDepth.avgSessionDurationMinutes || 0)}
                unit="min"
                trend={engagementDepth.avgSessionDurationTrend || []}
                color="#F59E0B"
                tooltip="Average duration of a chat session in minutes."
              /> */}

              <div className="flex items-center justify-between pt-3.5">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5 flex items-center">
                    Callback request rate
                    <InfoTooltip text="Percentage of active users who requested a human callback." width={290} />
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-semibold text-gray-900">{Number(engagementDepth.callbackRequestRate || 0)}%</span>
                    <span className="text-xs text-gray-400">{engagementDepth.callbackRequestCount || 0} requests</span>
                  </div>
                </div>
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-md ${
                    Number(engagementDepth.callbackRequestRate || 0) <= 5
                      ? 'bg-emerald-50 text-emerald-700'
                      : Number(engagementDepth.callbackRequestRate || 0) <= 15
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-rose-50 text-rose-700'
                  }`}
                >
                  {Number(engagementDepth.callbackRequestRate || 0) <= 5
                    ? 'Low — healthy'
                    : Number(engagementDepth.callbackRequestRate || 0) <= 15
                      ? 'Moderate'
                      : 'High — investigate'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patterns Tab */}
      {!loading && stats && activeTab === 'patterns' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">User patterns</h2>
            <p className="text-xs text-gray-500 mt-0.5">When, what language, and who your users are</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-1 text-sm font-semibold text-gray-900">
                  Peak usage hours
                  <InfoTooltip text="Distribution of sessions by hour of day." />
                </div>
                <span className="inline-flex items-center rounded-md border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                  Peak usage hour: {peakUsageHourLabel}
                </span>
              </div>
              <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
                {peakHours.map((h) => {
                  const pct = peakMax > 0 ? (h.sessions / peakMax) * 100 : 0;
                  const isTop = h.sessions >= peakMax * 0.85;
                  return (
                    <div key={`${h.hour}-${h.label}`} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400 w-9 text-right flex-shrink-0">{h.label}</span>
                      <div className="flex-1 h-3 bg-gray-100 rounded-sm overflow-hidden">
                        <div
                          className={`h-full rounded-sm transition-all ${isTop ? 'bg-indigo-500' : 'bg-indigo-200'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`text-[10px] w-6 text-right flex-shrink-0 ${isTop ? 'text-indigo-600 font-semibold' : 'text-gray-400'}`}>
                        {h.sessions}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-900 mb-0.5">Languages</p>
              <p className="text-xs text-gray-400 mb-4">By response count</p>
              {languages.length > 0 ? (
                <div className="space-y-3.5">
                  {languages.map((l, idx) => (
                    <div key={`${l.language}-${idx}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-gray-800">{l.languageName || l.language}</span>
                        <span className="text-gray-500">
                          {l.count} responses <span className="text-gray-300">· {Number(l.percentage || 0).toFixed(1)}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${l.percentage}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400 py-8 text-center">No language data</div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-900 mb-0.5">Audience breakdown</p>
              <p className="text-xs text-gray-400 mb-4">By industry (business nature)</p>

              {audienceBreakdown.length > 0 ? (
                <>
                  <div className="space-y-2.5">
                    {audienceBreakdown.map((a, idx) => (
                      <div key={`${a.value}-${idx}`} className="flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-indigo-500" />
                        <span className="flex-1 text-xs text-gray-800">{a.value}</span>
                        <span className="text-xs font-medium text-gray-600">{a.count}</span>
                        <span className="text-xs text-gray-400 w-10 text-right">{Number(a.pct || 0).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex h-2 rounded-full overflow-hidden mt-4">
                    {audienceBreakdown.map((a, idx) => (
                      <div key={`${a.value}-bar-${idx}`} style={{ width: `${a.pct}%`, background: '#6366F1', opacity: Math.max(0.25, 1 - idx * 0.08) }} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-400 py-8 text-center">No audience data</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {!loading && stats && activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="w-px h-6 bg-gray-200" />

            {filterChips.map((f) => (
              <button
                key={f.key}
                onClick={() => setUserFilter(f.key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                  userFilter === f.key
                    ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {f.label} <span className="opacity-60">({f.count})</span>
              </button>
            ))}

            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className={filterSelectClass(Boolean(selectedLanguage))}
            >
              <option value="">Language</option>
              {filterOptions.languages.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={selectedOccupation}
              onChange={(e) => setSelectedOccupation(e.target.value)}
              className={filterSelectClass(Boolean(selectedOccupation))}
            >
              <option value="">Occupation</option>
              {filterOptions.occupations.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={selectedBusinessNature}
              onChange={(e) => setSelectedBusinessNature(e.target.value)}
              className={filterSelectClass(Boolean(selectedBusinessNature))}
            >
              <option value="">Business Nature</option>
              {filterOptions.businessNatures.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <div className="flex-1" />

            <button
              onClick={() => toast.info('Export CSV coming soon')}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full table-fixed text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {userCols.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{ width: col.width }}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-100 cursor-pointer select-none hover:text-gray-600 transition-colors"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortCol === col.key ? (
                          sortDir === 'desc' ? <ChevronDown className="w-3 h-3 text-indigo-500" /> : <ChevronUp className="w-3 h-3 text-indigo-500" />
                        ) : null}
                      </span>
                    </th>
                  ))}
                  <th
                    style={{ width: '12%' }}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-100"
                  >
                    More Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowLoading ? (
                  <tr>
                    <td colSpan={userTableColSpan} className="px-4 py-12 text-center text-sm text-gray-400">
                      Loading users…
                    </td>
                  </tr>
                ) : sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={userTableColSpan} className="px-4 py-12 text-center text-sm text-gray-400">
                      No users match this filter
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((u) => {
                    const isExpanded = expandedUserId === u.userId;
                    const languageTags = Array.isArray(u.languages) ? u.languages : [];
                    const showLanguageBadges =
                      u.status !== 'never_chatted' && languageTags.length > 0;

                    return (
                      <React.Fragment key={u.userId}>
                        <tr className={`border-b border-gray-50 transition-colors ${isExpanded ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor(u.name)}`}>
                                {getInitials(u.name)}
                              </div>
                              <div className="min-w-0">
                                <span className="font-medium text-gray-900 truncate block">{u.name || '—'}</span>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3 text-xs text-gray-500">
                            <span className="truncate block">{u.email || '—'}</span>
                          </td>

                          <td className="px-4 py-3">
                            <StatusBadge status={(u.status as UserStatus) || 'inactive'} />
                          </td>

                          <td className="px-4 py-3">
                            <VerifiedBadge status={(u.verified as VerifiedStatus) || 'unverified'} />
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-700">
                            <span className="truncate block">{u.occupation || '—'}</span>
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-700">
                            <span className="truncate block">{u.businessNature || '—'}</span>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => setExpandedUserId((current) => (current === u.userId ? null : u.userId))}
                              className={`inline-flex items-center justify-center rounded-lg border p-2 transition-colors ${
                                isExpanded
                                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
                              }`}
                              aria-label={isExpanded ? `Hide details for ${u.name}` : `Show details for ${u.name}`}
                              title={isExpanded ? 'Hide details' : 'Show details'}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-white border-b border-gray-100">
                            <td colSpan={userTableColSpan} className="px-4 pb-4 pt-0">
                              <div className="rounded-xl border border-gray-200 bg-gray-100 px-4 py-3">
                                <div className="mb-3">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    Languages Chatted In
                                  </p>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {showLanguageBadges ? (
                                      languageTags.map((language) => (
                                        <LanguageBadge
                                          key={`${u.userId}-${language.value}`}
                                          languageCode={language.value}
                                          languageName={language.label}
                                        />
                                      ))
                                    ) : (
                                      <span className="text-sm font-medium text-gray-400">—</span>
                                    )}
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                <div>
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    Sessions
                                  </p>
                                  <p className={`mt-1 text-sm font-semibold ${u.sessions > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>
                                    {u.sessions || '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    Messages
                                  </p>
                                  <p className={`mt-1 text-sm font-semibold ${u.messages > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                                    {u.messages || '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    Registered
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-gray-700">
                                    {u.registered ? dayjs(u.registered).format('MMM D, YYYY') : '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    Last Active
                                  </p>
                                  <p className={`mt-1 text-sm font-semibold ${u.lastActive ? 'text-gray-700' : 'text-gray-400'}`}>
                                    {toRelativeTime(u.lastActive)}
                                  </p>
                                </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {tablePages > 1 && (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {tableTotal} total · page {tablePage} of {tablePages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => fetchUsersTable(tablePage - 1)}
                  disabled={tablePage <= 1 || rowLoading}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => fetchUsersTable(tablePage + 1)}
                  disabled={tablePage >= tablePages || rowLoading}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          <p className="text-center text-xs text-gray-300 pb-1">
            Showing {sortedRows.length > 0 ? (tablePage - 1) * tableLimit + 1 : 0}–{Math.min(tablePage * tableLimit, tableTotal)} of {tableTotal} users
          </p>
        </div>
      )}
    </div>
  );
};

export default UserStatistics;
